/**
 * Tableau de bord d'un projet (onglet `tableau-de-bord`) : KPI, retards, échéances à 7 jours,
 * jalons, avancement par lot, répartitions. Planche B (`CELLS_PLANCHE`/`CELL_*`).
 *
 * « Aujourd'hui » est UNIQUE : la date locale (`todayISO`). Le KPI « En retard » est donc le
 * nombre de la liste des retards calculée ici, pas `stats.overdue` (date UTC du serveur).
 *
 * Données : routes EXISTANTES uniquement — `/projects/:id/stats` (KPI, répartition par statut),
 * la liste complète des tâches (`useAllTasks` : retards, échéances, lots, responsables) et
 * `/portfolio/insights?projectId=` (météo et indicateurs, optionnels : leur absence n'empêche
 * pas le tableau de bord). Rechargement sur `reloadKey`.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TASK_STATUSES } from '@exiplan/shared-types';
import { api } from '../lib/api.js';
import { useAsync } from '../hooks/useAsync.js';
import { useAllTasks } from '../hooks/useAllTasks.js';
import {
  Empty,
  Eyebrow,
  Progress,
  StatusTag,
  TruncatedTasksWarning,
  labelForStatus,
  taskReference,
} from '../components/common.jsx';
import { addDays, todayISO } from '../lib/calendar.js';
import {
  byAssignee,
  overdueTasks,
  phases,
  upcomingDeadlines,
  upcomingMilestones,
} from '../lib/dashboard.js';
import { weatherClass, weatherKey } from '../lib/weather.js';
import { CELLS_PLANCHE, CELL_KPI, CELL_PLANCHE, TAG } from '../ui/theme.js';

/** Cellule KPI : libellé, valeur, contenu optionnel (barre). */
function Kpi({ label, value, children }) {
  return (
    <div className={`${CELL_KPI} flex flex-col gap-2`}>
      <span className="ticks" aria-hidden="true" />
      <span className="font-mono text-[10px] uppercase tracking-label text-ink-faint">{label}</span>
      <span className="text-2xl font-semibold tabular-nums text-ink">{value}</span>
      {children}
    </div>
  );
}

/** Cellule de section : titre + contenu. */
function Section({ title, children }) {
  return (
    <div className={`${CELL_PLANCHE} flex flex-col gap-3 bg-paper p-4`}>
      <span className="ticks" aria-hidden="true" />
      <Eyebrow>{title}</Eyebrow>
      {children}
    </div>
  );
}

/** Liste de tâches cliquables : référence, titre, complément à droite (`aside`). */
function TaskList({ rows, empty, projectKey, onOpenTask }) {
  if (rows.length === 0) return <Empty>{empty}</Empty>;
  return (
    <ul className="flex flex-col divide-y-2 divide-line">
      {rows.map(({ task, aside }) => (
        <li key={task.id}>
          <button
            type="button"
            className="flex w-full items-center gap-3 px-1 py-1.5 text-left hover:bg-surface"
            onClick={() => onOpenTask(task.id)}
          >
            <span className="font-mono text-[11px] text-ink-faint">
              {task.kind === 'milestone' ? '◆ ' : ''}
              {taskReference(projectKey, task.number)}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-ink">{task.title}</span>
            {aside}
          </button>
        </li>
      ))}
    </ul>
  );
}

const dateAside = (date) => <span className="font-mono text-[11px] text-ink-faint">{date}</span>;

export function DashboardView({ projectId, projectKey, onOpenTask, reloadKey }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language?.split('-')[0];
  const today = useMemo(() => todayISO(), []);

  const { data: stats, error: statsError } = useAsync(
    (signal) => api.get(`/projects/${projectId}/stats`, { signal }).then((r) => r.data),
    [projectId, reloadKey],
  );
  const {
    tasks: allTasks,
    truncated,
    error: tasksError,
  } = useAllTasks(projectId, { deps: [reloadKey] });
  // Météo et indicateurs : complément facultatif (fenêtre calculée par le serveur, date du jour
  // UTC — elle peut différer de `today` local autour de minuit), une erreur donne simplement « pas de météo ».
  const { data: insights } = useAsync(
    (signal) => {
      const params = new URLSearchParams({
        from: addDays(today, -28),
        to: addDays(today, 28),
        projectId,
      });
      return api
        .get(`/portfolio/insights?${params.toString()}`, { signal })
        .then((r) => r.data)
        .catch((error) => {
          if (error?.name === 'AbortError') throw error;
          return null;
        });
    },
    [projectId, reloadKey],
  );

  const tasks = useMemo(() => allTasks ?? [], [allTasks]);
  const indicators = insights?.indicators?.byProject?.[0] ?? null;

  const overdue = useMemo(() => overdueTasks(tasks, today), [tasks, today]);
  const upcoming = useMemo(() => upcomingDeadlines(tasks, today, 7), [tasks, today]);
  const milestones = useMemo(() => upcomingMilestones(tasks, today), [tasks, today]);
  const lots = useMemo(() => phases(tasks), [tasks]);
  const assignees = useMemo(() => byAssignee(tasks), [tasks]);

  if (statsError || tasksError) return <Empty>{t('dashboard.loadFailed')}</Empty>;
  if (!stats || allTasks === null) return null;

  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const percent = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 });
  const total = stats.total;
  const done = stats.byStatus?.done ?? 0;
  const workTotal = assignees.reduce((sum, entry) => sum + entry.count, 0);
  const weighted = stats.load?.weightedProgress ?? 0;

  return (
    <div className="flex flex-col gap-4">
      {truncated && <TruncatedTasksWarning />}

      <div className={`${CELLS_PLANCHE} grid-cols-2 lg:grid-cols-4`}>
        <Kpi label={t('dashboard.kpi.progress')} value={percent.format(weighted / 100)}>
          <Progress value={weighted} label={t('dashboard.kpi.progress')} />
        </Kpi>
        <Kpi
          label={t('dashboard.kpi.tasks')}
          value={t('dashboard.kpi.tasksValue', { done, total })}
        />
        <Kpi label={t('dashboard.kpi.overdue')} value={number.format(overdue.length)} />
        <Kpi
          label={t('dashboard.kpi.load')}
          value={t('dashboard.kpi.loadValue', {
            consumed: number.format(stats.load?.consumed ?? 0),
            total: number.format(stats.load?.total ?? 0),
          })}
        />
      </div>

      {indicators && (
        <div className={`${CELLS_PLANCHE} grid-cols-1`}>
          <Section title={t('dashboard.weather')}>
            <span className={`${TAG} w-fit border-line ${weatherClass(indicators.weather)}`}>
              <span aria-hidden="true">●</span>
              {t(weatherKey(indicators.weather))}
            </span>
            <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-ink-faint">{t('overview.indicators.onTime')}</dt>
                <dd className="font-semibold tabular-nums text-ink">
                  {indicators.onTime ? percent.format(indicators.onTime.value) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-ink-faint">{t('overview.indicators.overdue')}</dt>
                <dd className="font-semibold tabular-nums text-ink">
                  {t('overview.indicators.overdueRatio', {
                    count: indicators.overdue.count,
                    open: indicators.overdue.open,
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-ink-faint">{t('overview.indicators.scheduleGap')}</dt>
                <dd className="font-semibold tabular-nums text-ink">
                  {indicators.scheduleGap
                    ? t('overview.indicators.schedulePoints', {
                        value: indicators.scheduleGap.value,
                      })
                    : '—'}
                </dd>
              </div>
            </dl>
          </Section>
        </div>
      )}

      <div className={`${CELLS_PLANCHE} grid-cols-1 lg:grid-cols-2`}>
        <Section title={t('dashboard.overdueTitle')}>
          <TaskList
            projectKey={projectKey}
            onOpenTask={onOpenTask}
            empty={t('dashboard.overdueEmpty')}
            rows={overdue.map(({ task, daysLate }) => ({
              task,
              aside: (
                <span className="font-mono text-[11px] text-accent-ink">
                  <span aria-hidden="true">▲ </span>
                  {t('dashboard.daysLate', { count: daysLate })}
                </span>
              ),
            }))}
          />
        </Section>

        <Section title={t('dashboard.upcomingTitle')}>
          <TaskList
            projectKey={projectKey}
            onOpenTask={onOpenTask}
            empty={t('dashboard.upcomingEmpty')}
            rows={upcoming.map((task) => ({ task, aside: dateAside(task.dueDate) }))}
          />
        </Section>

        <Section title={t('dashboard.milestonesTitle')}>
          <TaskList
            projectKey={projectKey}
            onOpenTask={onOpenTask}
            empty={t('dashboard.milestonesEmpty')}
            rows={milestones.map((task) => ({
              task,
              aside: dateAside(task.dueDate ?? task.startDate),
            }))}
          />
        </Section>

        <Section title={t('dashboard.phasesTitle')}>
          {lots.length === 0 ? (
            <Empty>{t('dashboard.phasesEmpty')}</Empty>
          ) : (
            <ul className="flex flex-col gap-3">
              {lots.map((lot) => (
                <li key={lot.id} className="flex flex-col gap-1">
                  <button
                    type="button"
                    className="flex items-center gap-3 text-left hover:bg-surface"
                    onClick={() => onOpenTask(lot.id)}
                  >
                    <span className="font-mono text-[11px] text-ink-faint">
                      {taskReference(projectKey, lot.number)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{lot.title}</span>
                    <StatusTag status={lot.status} />
                  </button>
                  <Progress
                    value={lot.progress ?? 0}
                    label={t('dashboard.phaseProgress', { title: lot.title })}
                  />
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={t('dashboard.byStatusTitle')}>
          <ul className="flex flex-col gap-2">
            {TASK_STATUSES.map((status) => {
              const count = stats.byStatus?.[status] ?? 0;
              const label = labelForStatus(status, t);
              return (
                <li key={status} className="flex flex-col gap-1">
                  <span className="text-sm text-ink">{t('dashboard.share', { label, count })}</span>
                  <Progress value={total ? (count / total) * 100 : 0} label={label} />
                </li>
              );
            })}
          </ul>
        </Section>

        <Section title={t('dashboard.byAssigneeTitle')}>
          {assignees.length === 0 ? (
            <Empty>{t('dashboard.byAssigneeEmpty')}</Empty>
          ) : (
            <ul className="flex flex-col gap-2">
              {assignees.map((entry) => {
                const label = entry.name ?? t('taskDrawer.noAssignee');
                return (
                  <li key={entry.key ?? 'unassigned'} className="flex flex-col gap-1">
                    <span className="text-sm text-ink">
                      {t('dashboard.share', { label, count: entry.count })}
                    </span>
                    <Progress
                      value={workTotal ? (entry.count / workTotal) * 100 : 0}
                      label={label}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}
